/**
 * Phase L — Rental detail Invoices section.
 *
 * Mounted inside AdminRentalDetailPage. Title is path-aware:
 *   - "Invoices for {customerName} on this rental" when URL has ?from=customer
 *   - "Invoices for this rental"                  otherwise
 * Data is identical (filtered by rental_id) — only the label changes.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Plus, ArrowUpRight, Eye } from "lucide-react";

const STATUS_CHIPS = [
    { key: "", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "viewed", label: "Viewed" },
    { key: "paid", label: "Paid" },
    { key: "overdue", label: "Overdue" },
    { key: "void", label: "Void" },
];

const STATUS_TONE = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    viewed: "bg-violet-50 text-violet-800 ring-violet-200",
    paid: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    void: "bg-slate-100 text-slate-500 ring-slate-200",
};

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDay(s) { if (!s) return "—"; return s.slice(0, 10); }

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const m = Math.floor(diff / 60000);
        if (m < 60) return `${Math.max(m, 1)}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
    } catch { return iso; }
}

function StatusBadge({ status }) {
    const tone = STATUS_TONE[status] || STATUS_TONE.draft;
    return (
        <span
            data-testid={`rental-invoice-status-${status}`}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tone}`}
        >
            {status?.toUpperCase()}
        </span>
    );
}

export default function RentalInvoicesSection({ rentalId, customerId, onCreateInvoice }) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const fromCustomer = params.get("from") === "customer";
    // Phase INV-FILTER-DEFAULT-DRAFT (2026-07-29) — default the chip
    // to "Draft" on first visit; user's last pick is remembered per
    // surface via localStorage.
    const LS_KEY = "invoices.list.rental.filter";
    const urlStatus = params.get("inv_status");
    const status = urlStatus ?? (localStorage.getItem(LS_KEY) ?? "draft");

    const [customerName, setCustomerName] = useState(null);
    useEffect(() => {
        if (!fromCustomer || !customerId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/customers/${customerId}`);
                if (!cancelled) setCustomerName(data?.company_name || data?.dba || null);
            } catch {
                // Non-fatal — fall back to the standalone title.
            }
        })();
        return () => { cancelled = true; };
    }, [fromCustomer, customerId]);

    const title = fromCustomer && customerName
        ? `Invoices for ${customerName} on this rental`
        : "Invoices for this rental";

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const updateStatus = useCallback((next) => {
        // Phase INV-FILTER-DEFAULT-DRAFT — persist the choice so this
        // surface reopens on the same filter next visit. Store empty
        // string when the user picks "All".
        try { localStorage.setItem(LS_KEY, next || ""); } catch { /* noop */ }
        const q = new URLSearchParams(location.search);
        if (!next) q.delete("inv_status"); else q.set("inv_status", next);
        navigate({ pathname: location.pathname, search: `?${q.toString()}` }, { replace: false });
    }, [location, navigate]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const reqParams = { rental_id: rentalId, limit: 50, sort: "-issue_date" };
                if (status) reqParams.status = status;
                const { data } = await api.get("/admin/invoices", { params: reqParams });
                if (!cancelled) {
                    setItems(data.items || []);
                    setTotal(data.total || 0);
                }
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [rentalId, status]);

    return (
        <Card data-testid="rental-invoices-section">
            <CardContent className="p-5 space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2
                            data-testid="rental-invoices-section-title"
                            className="text-base font-semibold text-slate-900 flex items-center gap-2"
                        >
                            <Receipt size={16} className="text-blue-900" />
                            {title}
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {loading ? "Loading…" : `${total} invoice${total === 1 ? "" : "s"} found.`}
                        </p>
                    </div>
                    {onCreateInvoice && (
                        <Button
                            type="button"
                            size="sm"
                            onClick={onCreateInvoice}
                            data-testid="rental-invoices-create-btn"
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        >
                            <Plus size={14} /> Create invoice for this rental
                        </Button>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    {STATUS_CHIPS.map((c) => (
                        <button
                            key={c.key || "all"}
                            type="button"
                            data-testid={`rental-invoices-filter-${c.key || "all"}`}
                            onClick={() => updateStatus(c.key)}
                            className={
                                "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 transition-colors " +
                                ((status === c.key || (!status && c.key === ""))
                                    ? "bg-blue-900 text-white ring-blue-900"
                                    : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50")
                            }
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {!loading && items.length === 0 && (
                    <p
                        data-testid="rental-invoices-empty"
                        className="px-4 py-10 text-center text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg"
                    >
                        No invoices for this rental yet.
                    </p>
                )}

                {!loading && items.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60">
                                <tr className="border-b border-slate-200">
                                    <th className="text-left px-3 py-2.5">Invoice #</th>
                                    <th className="text-left px-2 py-2.5">Status</th>
                                    <th className="text-left px-2 py-2.5">Issued</th>
                                    <th className="text-left px-2 py-2.5">Due</th>
                                    <th className="text-right px-2 py-2.5">Total</th>
                                    <th className="text-right px-2 py-2.5">Paid / Refunded</th>
                                    <th className="text-left px-3 py-2.5">Viewed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((inv) => {
                                    const paid = inv.amount_paid_cents || 0;
                                    const refunded = inv.amount_refunded_cents || 0;
                                    return (
                                        <tr
                                            key={inv.id}
                                            data-testid={`rental-invoice-row-${inv.id}`}
                                            onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                                            className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 cursor-pointer"
                                        >
                                            <td className="px-3 py-3 font-medium text-slate-900">
                                                <Link
                                                    to={`/admin/invoices/${inv.id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="hover:text-blue-900 inline-flex items-center gap-1"
                                                >
                                                    {inv.invoice_number}
                                                    <ArrowUpRight size={12} className="text-slate-400" />
                                                </Link>
                                            </td>
                                            <td className="px-2 py-3"><StatusBadge status={inv.status} /></td>
                                            <td className="px-2 py-3 text-slate-600">{fmtDay(inv.issue_date)}</td>
                                            <td className="px-2 py-3 text-slate-600">{fmtDay(inv.due_date)}</td>
                                            <td className="px-2 py-3 text-right tabular-nums text-slate-900">{fmtMoney(inv.total_cents)}</td>
                                            <td className="px-2 py-3 text-right tabular-nums text-slate-600">
                                                {(paid > 0 || refunded > 0) ? (
                                                    <span>
                                                        {fmtMoney(paid)} paid
                                                        {refunded > 0 && <span className="text-violet-700"> · {fmtMoney(refunded)} refunded</span>}
                                                    </span>
                                                ) : "—"}
                                            </td>
                                            <td className="px-3 py-3">
                                                {inv.last_viewed_at ? (
                                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" title={inv.last_viewed_at}>
                                                        <Eye size={11} /> {fmtRel(inv.last_viewed_at)}
                                                    </span>
                                                ) : (
                                                    <span className="text-[11px] text-slate-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
